/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';

/**
 * 规则类型
 */
export enum RuleTypeE {
    /**
     * 只拒绝
     */
    Denied = '0',
    /**
     * 只允许
     */
    Allowed = '1'
}

/**
 * 共享类型
 */
export enum ShareMoTypeE {
    NFS = 16401,
    CIFS = 16402
}

/**
 * 文件操作类型
 */
export enum FileOperationE {
    CLOSE = 1,
    CREATE = 2,
    CREATE_DIR = 3,
    DELETE = 4,
    DELETE_DIR = 5,
    GETATTR = 6,
    LINK = 7,
    LOOKUP = 8,
    OPEN = 9,
    READ = 10,
    WRITE = 11,
    RENAME = 12,
    RENAME_DIR = 13,
    SETATTR = 14,
    SYMLINK = 15
}

@Injectable({ providedIn: 'root' })
export class ExtensionFilteringStrService {
    get = this.i18nService.get;

    i18n = {
        id: this.get('common.ID_column'),
        fileNameExtensionFilter: this.get('service.share_task_filenameextensionfiltering_label'),
        fileNameExtension: this.get('service.share_task_filenameextensionfilteringName_lab'),
        ruleType: this.get('service.share_task_filenameextensionfilteringRule_lab'),
        operationType: this.get('service.share_task_filenameextensionfilteringRule_operationType_lab'),
        extensionFiltering: this.get('service.share_task_filenameextensionfilteringRule_tab'),
        commaValue: this.get('common.comma_value'),

        deleteExtensionFilteringTitle: this.get('service.share_task_filenameextensionfiltering_remove_title'),
        createExtensionFilteringTitle: this.get('service.share_task_filenameextensionfiltering_add_title'),
        modifyExtensionFilteringTitle: this.get('service.share_task_filenameextensionfiltering_modify_title'),

        extensionFilteringRuleTip: this.get('service.share_task_filenameextensionfiltering_description'),
        extensionNameTips: this.get('service.share_task_filenameextensionfiltering_tip', [127]),
        deleteExtensionFilteringTip: this.get('service.share_task_filenameextensionfiltering_remove_confirm_msg'),
        ruleTypeTip: this.get('service.share_task_filenameextensionfilteringRule_tip'),
        operationTypeTip: this.get('service.share_task_filenameextensionfilteringRule_operationType_tip'),

        rulesMaxLengthErr: (value: number): string => this.get('common.totalLenth_valid', [value]),
        extensionErr: this.get('service.share_task_filenameextensionfiltering_valid'),
        operationsErr: this.get('common.selectOneObject_lab'),

        addBtn: this.get('common.add_button'),
        removeBtn: this.get('common.remove_button'),
        deleteBtn: this.get('common.delete_button'),
        modifyBtn: this.get('common.modify_button'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),

        deleteExtensionFilteringAction: (value: string): string => this.get('rest.removeFilenameextensionfiltering_msg', [value]),
        modifyExtensionFilteringAction: (value: string): string => this.get('rest.modifyFilenameextensionfiltering_msg', [value]),
        createExtensionFilteringAction: (value: string): string => this.get('rest.addFilenameextensionfiltering_msg', [value]),
    };

    /**
     * 规则类型
     */
    ruleTypeOpt: SelectOption[] = [
        {
            value: RuleTypeE.Denied,
            label: this.get('service.share_task_filenameextensionfilteringRule_deny_value'),
        },
        {
            value: RuleTypeE.Allowed,
            label: this.get('service.share_task_filenameextensionfilteringRule_allow_value'),
        },
    ];

    /**
     * cifs文件操作类型
     */
    cifsFileOperationsOpt: SelectOption[] = [
        {
            value: FileOperationE.CREATE,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Create_value'),
        },
        {
            value: FileOperationE.CREATE_DIR,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_createDir_value'),
        },
        {
            value: FileOperationE.OPEN,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Open_value'),
        },
        {
            value: FileOperationE.DELETE,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Delete_value'),
        },
        {
            value: FileOperationE.DELETE_DIR,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_deleteDir_value'),
        },
        {
            value: FileOperationE.CLOSE,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Close_value'),
        },
        {
            value: FileOperationE.RENAME,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Rename_value'),
        },
        {
            value: FileOperationE.RENAME_DIR,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_renameDir_value'),
        },
        {
            value: FileOperationE.SETATTR,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_SetProperty_value'),
        },
        {
            value: FileOperationE.READ,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Read_value'),
        },
        {
            value: FileOperationE.WRITE,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Write_value'),
        },
        {
            value: FileOperationE.GETATTR,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_GetProperty_value'),
        },
    ];

    /**
     * nfs文件操作类型
     */
    nfsFileOperationsOpt: SelectOption[] = [
        {
            value: FileOperationE.CREATE,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Create_value'),
        },
        {
            value: FileOperationE.CREATE_DIR,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_createDir_value'),
        },
        {
            value: FileOperationE.OPEN,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Open_value'),
        },
        {
            value: FileOperationE.DELETE,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Delete_value'),
        },
        {
            value: FileOperationE.DELETE_DIR,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_deleteDir_value'),
        },
        {
            value: FileOperationE.CLOSE,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Close_value'),
        },
        {
            value: FileOperationE.RENAME,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Rename_value'),
        },
        {
            value: FileOperationE.RENAME_DIR,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_renameDir_value'),
        },
        {
            value: FileOperationE.SETATTR,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_SetProperty_value'),
        },
        {
            value: FileOperationE.READ,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Read_value'),
        },
        {
            value: FileOperationE.WRITE,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_Write_value'),
        },
        {
            value: FileOperationE.GETATTR,
            label: this.get('service.fileSystem_term_create_EnableAuditLog_GetProperty_value'),
        },
        {
            value: FileOperationE.LINK,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_createHardLink_value'),
        },
        {
            value: FileOperationE.SYMLINK,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_createSymlink_value'),
        },
        {
            value: FileOperationE.LOOKUP,
            label: this.get('service.share_task_filenameextensionfilteringRule_operationType_lookup_value'),
        },
    ];

    constructor(
        private i18nService: I18nService,
        private utilService: UtilService,
    ) { }

    fileOperationRender = (operations: string, shareType: ShareMoTypeE): string => {
        if (!operations) {
            return UNPLACEHOLDER;
        }

        return operations
            .split(',')
            .map(
                (value) => this.utilService.getLabelFromOptions(
                    shareType === ShareMoTypeE.NFS ? this.nfsFileOperationsOpt : this.cifsFileOperationsOpt,
                    Number(value),
                ),
            )
            .join(this.i18n.commaValue);
    };
}
